/**
 * Page /auth/verify — landing post-magic-link.
 *
 * Idéalement on n'arrive jamais ici : l'API GET /api/auth/verify pose le
 * cookie + redirect direct vers `${PUBLIC_URL}/${nextPath}`. Mais si le
 * front est servi en relatif sans backend (eg. lien copié-collé), on
 * redirige côté client en parlant à l'API.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { setSessionToken } from '../lib/api-client';

export function AuthVerify() {
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setError('Token manquant.');
      return;
    }

    // Échange le magic-link contre un JWT de session que l'on stocke côté
    // navigateur (auth par en-tête Bearer). Indépendant du cookie tiers, donc
    // fonctionne sur tous les navigateurs (Safari, mode privé, mobile). L'API
    // pose aussi le cookie en parallèle pour ceux qui l'acceptent.
    const apiBase = import.meta.env.VITE_API_URL ?? '';
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/auth/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          let msg = 'Lien invalide ou expire.';
          try {
            const j = (await res.json()) as { message?: string };
            if (j.message) msg = j.message;
          } catch {
            /* ignore */
          }
          if (!cancelled) setError(msg);
          return;
        }
        const data = (await res.json()) as { token?: string; nextPath?: string };
        if (data.token) setSessionToken(data.token);
        if (cancelled) return;
        setDone(true);
        // Vraie navigation pour repartir sur une app fraîchement authentifiée.
        window.location.href = data.nextPath && data.nextPath.startsWith('/') ? data.nextPath : '/';
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erreur de connexion.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="tc-panel p-8 w-full max-w-md text-center">
        {error ? (
          <>
            <div className="tc-label text-red-400 mb-2">verification failed</div>
            <h1 className="text-xl font-display tracking-tight mb-3">Lien invalide</h1>
            <p className="text-sm text-steel-300 mb-4">{error}</p>
            <Link
              to="/login"
              className="text-2xs font-mono uppercase tracking-widest text-amber-500 hover:text-amber-400"
            >
              ← redemander un lien
            </Link>
          </>
        ) : done ? (
          <>
            <div className="tc-label mb-2">verifying</div>
            <p className="text-sm text-steel-500 font-mono">redirection en cours…</p>
          </>
        ) : (
          <p className="text-sm text-steel-500 font-mono">verification…</p>
        )}
      </div>
    </div>
  );
}
